import React, { useState } from "react";
import { api } from "../config/api";
import GoogleLoginButton from "./GoogleLoginButton";
import OTPEntryPopup from "./OTPEntryPopup";
import "./PhoneVerificationPopup.css";

/**
 * PhoneVerificationPopup
 *
 * The site's first-login screen. Offers two paths:
 *   1. Mobile + OTP (existing flow, unchanged)
 *   2. Continue with Google (new flow)
 *
 * For the Google path:
 *   - Brand-new Google user: log them in directly (server creates user)
 *   - Returning Google user: log them in directly (server finds user)
 *   - Phone user with same email as Google: server returns link_required;
 *     FE shows inline mobile+OTP, then calls /api/auth/google/link to
 *     merge the accounts.
 */
const PhoneVerificationPopup = ({
  isOpen,
  onClose,
  onPhoneVerified,
  onGoogleSignIn,
  onShowRegistration,
  onSwitchToGuest,
}) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isRecaptchaChecked, setIsRecaptchaChecked] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  // Google login state
  const [googleLoading, setGoogleLoading] = useState(false);
  const [linkState, setLinkState] = useState(null);
  // linkState shape: { userId, email, verifiedMobile? }

  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorPopup(true);
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    if (!phoneNumber || !isRecaptchaChecked) {
      showError("Please enter phone number and complete reCAPTCHA");
      return;
    }

    try {
      setIsChecking(true);

      const cleanPhone = phoneNumber.replace(/\s+/g, "");

      const result = await api.checkUserByMobile(cleanPhone);

      if (result.exists) {
        try {
          await api.sendOTP(cleanPhone);
          onPhoneVerified(cleanPhone);
        } catch (otpError) {
          console.error("Error sending OTP:", otpError);
          showError(
            otpError.message || "Failed to send OTP. Please try again.",
          );
        }
      } else {
        onShowRegistration(cleanPhone);
      }
    } catch (error) {
      console.error("Error checking user:", error);
      showError(
        error.message || "Failed to verify phone number. Please try again.",
      );
    } finally {
      setIsChecking(false);
    }
  };

  /**
   * Google OAuth callback — receives the ID token from the popup.
   */
  const handleGoogleSuccess = async (idToken) => {
    try {
      setGoogleLoading(true);
      const result = await api.googleLogin(idToken);

      // Case A or C: existing Google user OR new user — straight login
      if (result.user && !result.linkRequired) {
        // Defer to parent which will save to BookingContext + localStorage
        if (onGoogleSignIn) onGoogleSignIn(result.user);
        return;
      }

      // Case B: link_required — phone user with same email exists
      if (result.linkRequired && result.userId) {
        setLinkState({
          userId: result.userId,
          email: result.email,
          idToken,
          step: "enter_mobile",
        });
        return;
      }

      // Unexpected response
      showError("Google login completed but server returned no user. Please try again.");
    } catch (err) {
      console.error("Google login error:", err);
      showError(
        err?.response?.error ||
          err?.message ||
          "Google sign-in failed. Please try again.",
      );
    } finally {
      setGoogleLoading(false);
    }
  };

  /**
   * Link flow: user is in link_required state, enters mobile, sends OTP,
   * enters OTP, verifies, then calls /api/auth/google/link.
   */
  const handleLinkMobileSubmit = async (mobile) => {
    if (!linkState || linkState.step !== "enter_mobile") return;
    try {
      await api.sendOTP(mobile);
      setLinkState({
        ...linkState,
        verifiedMobile: mobile,
        step: "enter_otp",
      });
    } catch (err) {
      showError(
        err?.message || "Failed to send OTP. Please try again.",
      );
    }
  };

  const handleLinkOtpSubmit = async (otp) => {
    if (!linkState || linkState.step !== "enter_otp") return;
    try {
      // Verify OTP using existing endpoint
      const verifyResult = await api.verifyOTP(
        linkState.verifiedMobile,
        otp,
      );
      if (!verifyResult || !verifyResult.verified) {
        showError(
          (verifyResult && verifyResult.error) ||
            "OTP verification failed. Please try again.",
        );
        return;
      }

      // OTP verified — link the Google account
      const linkResult = await api.linkGoogleAccount({
        userId: linkState.userId,
        idToken: linkState.idToken,
        verifiedMobile: linkState.verifiedMobile,
      });

      if (linkResult.user) {
        if (onGoogleSignIn) onGoogleSignIn(linkResult.user);
        setLinkState(null);
      } else {
        showError("Failed to link Google account. Please try again.");
      }
    } catch (err) {
      console.error("Link Google error:", err);
      showError(
        err?.response?.error ||
          err?.message ||
          "Failed to link Google account. Please try again.",
      );
    }
  };

  const handleCancelLink = () => {
    setLinkState(null);
  };

  if (!isOpen) return null;

  return (
    <div className="phone-verification-overlay">
      <div className="phone-verification-popup">
        <button className="close-btn" onClick={onClose}>
          ✕
        </button>

        <div className="simple-hero">
          <div className="floating-elements">
            <div className="floating-icon">🎬</div>
            <div className="floating-icon">🍿</div>
            <div className="floating-icon">🎫</div>
          </div>
        </div>

        <div className="popup-content">
          <h2 className="popup-title">Sign In / Sign Up</h2>
          <p className="popup-subtitle">
            {linkState
              ? "Verify your mobile to link your Google account"
              : "Enter your mobile number to continue. We'll send you an OTP for verification."}
          </p>

          {/* ── Link-required mode (after a Google email matches a phone user) ── */}
          {linkState && linkState.step === "enter_mobile" && (
            <LinkMobileStep
              email={linkState.email}
              onSubmit={handleLinkMobileSubmit}
              onCancel={handleCancelLink}
            />
          )}

          {linkState && linkState.step === "enter_otp" && (
            <LinkOtpStep
              email={linkState.email}
              mobile={linkState.verifiedMobile}
              onSubmit={handleLinkOtpSubmit}
              onResend={async () => {
                try {
                  await api.sendOTP(linkState.verifiedMobile);
                } catch (err) {
                  showError(err?.message || "Failed to resend OTP.");
                }
              }}
              onCancel={handleCancelLink}
            />
          )}

          {/* ── Normal mode: Mobile OTP + Google ── */}
          {!linkState && (
            <>
              {/* Google button first (recommended path) */}
              <div className="google-section">
                <GoogleLoginButton
                  onSuccess={handleGoogleSuccess}
                  disabled={googleLoading || isChecking}
                />
                {googleLoading && (
                  <div className="google-loading">Signing in…</div>
                )}
              </div>

              <div className="divider">
                <span className="divider-text">OR</span>
              </div>

              {/* Mobile OTP form (existing flow) */}
              <form
                onSubmit={handlePhoneSubmit}
                className="phone-form"
                autoComplete="off"
              >
                <div className="phone-input-container">
                  <div className="phone-input-wrapper">
                    <div className="country-code">
                      <span className="country-flag">IN</span>
                      <span className="country-dial">+91</span>
                    </div>
                    <input
                      type="tel"
                      placeholder="Enter mobile number"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="phone-input"
                      required
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="recaptcha-container">
                  <label className="recaptcha-checkbox">
                    <input
                      type="checkbox"
                      checked={isRecaptchaChecked}
                      onChange={(e) =>
                        setIsRecaptchaChecked(e.target.checked)
                      }
                    />
                    <span className="recaptcha-text">I'm not a robot</span>
                  </label>
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="proceed-btn"
                    disabled={isChecking || googleLoading}
                  >
                    {isChecking ? "Checking..." : "Proceed"}
                  </button>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={onClose}
                    disabled={isChecking || googleLoading}
                  >
                    Continue without signing in
                  </button>
                </div>
              </form>

              {onSwitchToGuest && (
                <button
                  className="switch-to-guest-btn"
                  onClick={onSwitchToGuest}
                  type="button"
                >
                  Continue as Guest
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {showErrorPopup && (
        <div
          className="error-popup-overlay"
          onClick={() => setShowErrorPopup(false)}
        >
          <div
            className="error-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="error-popup-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <div className="error-popup-message">{errorMessage}</div>
            <button
              className="error-popup-button"
              onClick={() => setShowErrorPopup(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneVerificationPopup;

// ─────────────────────────────────────────────────────────────────
// Sub-components for the Google-account linking flow
// ─────────────────────────────────────────────────────────────────

const LinkMobileStep = ({ email, onSubmit, onCancel }) => {
  const [mobile, setMobile] = useState("");
  return (
    <div className="link-flow">
      <p className="link-flow-info">
        We found an existing account with the email <strong>{email}</strong>.
        To link your Google account, please verify your mobile number.
      </p>
      <div className="phone-input-wrapper">
        <div className="country-code">
          <span className="country-flag">IN</span>
          <span className="country-dial">+91</span>
        </div>
        <input
          type="tel"
          className="phone-input"
          placeholder="Enter your 10-digit mobile"
          value={mobile}
          onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && mobile.length === 10) onSubmit(mobile);
          }}
          autoComplete="off"
        />
      </div>
      <div className="form-actions">
        <button
          type="button"
          className="proceed-btn"
          onClick={() => mobile.length === 10 && onSubmit(mobile)}
          disabled={mobile.length !== 10}
        >
          Send OTP
        </button>
        <button type="button" className="cancel-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
};

const LinkOtpStep = ({ email, mobile, onSubmit, onResend, onCancel }) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (idx, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[idx] = value;
    setOtp(next);
    if (value && idx < 5) {
      const nextInput = document.getElementById(`link-otp-${idx + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      const prevInput = document.getElementById(`link-otp-${idx - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleSubmit = async () => {
    const code = otp.join("");
    if (code.length !== 6) return;
    try {
      setSubmitting(true);
      await onSubmit(code);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="link-flow">
      <p className="link-flow-info">
        Enter the 6-digit OTP sent to your mobile <strong>+91 {mobile}</strong>{" "}
        (linked to <strong>{email}</strong>).
      </p>
      <div className="otp-input-container">
        {otp.map((digit, idx) => (
          <input
            key={idx}
            id={`link-otp-${idx}`}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(idx, e.target.value)}
            onKeyDown={(e) => handleKeyDown(idx, e)}
            className="otp-input"
            autoFocus={idx === 0}
            autoComplete="off"
          />
        ))}
      </div>
      <div className="form-actions">
        <button
          type="button"
          className="proceed-btn"
          onClick={handleSubmit}
          disabled={otp.join("").length !== 6 || submitting}
        >
          {submitting ? "Linking..." : "Verify & Link"}
        </button>
        <button type="button" className="cancel-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <button type="button" className="link-flow-resend" onClick={onResend}>
        Resend OTP
      </button>
    </div>
  );
};